// The circuit as a GRAPH. Two verbs act on it — delete a length, draw a new one
// — and every destination's cable is DERIVED from it afterwards by the cheapest
// way home. That derivation is the whole point: until paths were a product of
// the graph rather than the source of truth, "delete this and redraw it" could
// not be expressed at all.
//
// Metres are safe under it because `circuitMetres` already counts each distinct
// segment ONCE across a circuit's paths — a circuit's length is already the
// length of its edge set, which is exactly what a graph measures.

import type {
  ConnectGraphPath,
  DeleteGraphEdges,
  DerivePaths,
  EdgeKey,
  EdgeMarksOf,
  GraphFromPaths,
  MoveGraphNode,
  NearestGraphEdge,
  NearestGraphNode,
  NodeKey,
  OrthogonalRun,
  RouteGraph,
  RoutePoint,
  SnapDragPoint,
  FootOnGraphEdge,
  LandOnGraph,
  MergeGraphNodes,
  SplitGraphEdge,
  WeldGraph,
  OrthogonalPath,
  StrandedEdgesOf,
} from "./types";

/**
 * Two points this close are the SAME node. A run's destinations share a trunk by
 * coordinate and nothing else, so coincidence is the only thing that identifies
 * a node — generous enough for points that were computed rather than copied,
 * far tighter than any two real corners.
 */
/** A run has tens of lengths; this is a ceiling, never a working limit. */
const MAX_WELD_PASSES = 200;

export const COINCIDENT = 0.5;

/**
 * A length shorter than this gets no ⊕. It is a plan distance and deliberately
 * generous: the marks themselves no longer grow with the drawing, so a length
 * too cramped for a ⊕ at one zoom is roomy at the next — and a mark that came
 * and went as he zoomed would be worse than one that is simply always there on
 * the lengths worth splitting.
 */
export const ADD_MIN_LENGTH = 60;

/** Within this of square with the point before it, a drawn point goes square. */
export const SQUARE_SNAP = 10;

const BUCKETS_PER_POINT = 2;

const keyOf = (point: RoutePoint): NodeKey =>
  `${Math.round(point.x * BUCKETS_PER_POINT)}:${Math.round(point.y * BUCKETS_PER_POINT)}`;

export const edgeKeyOf = (a: NodeKey, b: NodeKey): EdgeKey =>
  a < b ? `${a}~${b}` : `${b}~${a}`;

const copy = (point: RoutePoint): RoutePoint => ({ x: point.x, y: point.y });

const distance = (a: RoutePoint, b: RoutePoint): number =>
  Math.hypot(b.x - a.x, b.y - a.y);

/** Closest point on segment ab to p. */
const closestOnSegment = (
  p: RoutePoint,
  a: RoutePoint,
  b: RoutePoint,
): RoutePoint => {
  const abx = b.x - a.x;
  const aby = b.y - a.y;
  const length2 = abx * abx + aby * aby;
  if (length2 === 0) return copy(a);
  const t = Math.max(
    0,
    Math.min(1, ((p.x - a.x) * abx + (p.y - a.y) * aby) / length2),
  );
  return { x: a.x + t * abx, y: a.y + t * aby };
};

export const graphFromPaths: GraphFromPaths = (paths) => {
  const points: Record<NodeKey, RoutePoint> = {};
  const edges: Record<EdgeKey, [NodeKey, NodeKey]> = {};
  const deviceKeys: Record<string, NodeKey> = {};

  const put = (point: RoutePoint): NodeKey => {
    const key = keyOf(point);
    if (!points[key]) points[key] = copy(point);
    return key;
  };

  // Every cable of a run leaves the same board, and it is waypoint 0 of each of
  // them. A device with no cable left has only itself, so the first path with a
  // SECOND waypoint is the one that still knows where home is — and if not one
  // of them does, the run has no cable at all and there is no board to name.
  // Falling back to the first path would make some device's own position the
  // board and quietly re-root the whole run on it.
  const anchored = paths.find((path) => path.waypoints.length > 1);
  const boardKey = anchored ? put(anchored.waypoints[0]) : "";

  for (const path of paths) {
    const { waypoints } = path;
    if (!waypoints.length) continue;
    let previous = put(waypoints[0]);
    for (let i = 1; i < waypoints.length; i += 1) {
      const current = put(waypoints[i]);
      if (current !== previous) edges[edgeKeyOf(previous, current)] = [previous, current];
      previous = current;
    }
    // The LAST waypoint is the device, and its node is kept whatever happens to
    // the cable — he has to have something to draw back to.
    deviceKeys[path.destinationId] = put(waypoints[waypoints.length - 1]);
  }

  return { points, edges, boardKey, deviceKeys };
};

/** Drop nodes nothing hangs off. The board and the devices always stay. */
const pruned = (graph: RouteGraph): RouteGraph => {
  const kept = new Set<NodeKey>([graph.boardKey, ...Object.values(graph.deviceKeys)]);
  for (const [a, b] of Object.values(graph.edges)) {
    kept.add(a);
    kept.add(b);
  }
  const points: Record<NodeKey, RoutePoint> = {};
  for (const key of Object.keys(graph.points)) {
    if (kept.has(key)) points[key] = graph.points[key];
  }
  return { ...graph, points };
};

/**
 * A key nothing else is using. Node keys start life as coordinate buckets so
 * that coincident waypoints MERGE into one node, and are opaque from then on —
 * a corner that moves keeps its key, or every edge naming it is orphaned.
 */
const freshKey = (points: Record<NodeKey, RoutePoint>, at: RoutePoint): NodeKey => {
  const base = keyOf(at);
  if (!points[base]) return base;
  for (let n = 2; ; n += 1) {
    const candidate = `${base}#${n}`;
    if (!points[candidate]) return candidate;
  }
};

export const deleteGraphEdges: DeleteGraphEdges = ({ graph, edgeKeys }) => {
  const gone = edgeKeys.filter((key) => graph.edges[key]);
  if (!gone.length) return graph;
  const edges = { ...graph.edges };
  for (const key of gone) delete edges[key];
  return pruned({ ...graph, edges });
};

export const moveGraphNode: MoveGraphNode = ({ graph, key, to }) => {
  if (!graph.points[key]) return graph;
  return { ...graph, points: { ...graph.points, [key]: copy(to) } };
};

export const nearestGraphNode: NearestGraphNode = ({ graph, at, radius }) => {
  let best: NodeKey | null = null;
  let bestGap = radius;
  for (const [key, point] of Object.entries(graph.points)) {
    const gap = distance(point, at);
    if (gap > bestGap) continue;
    bestGap = gap;
    best = key;
  }
  return best;
};

export const nearestGraphEdge: NearestGraphEdge = ({ graph, at, radius }) => {
  let best: ReturnType<NearestGraphEdge> = null;
  let bestGap = radius;
  for (const [edgeKey, [a, b]] of Object.entries(graph.edges)) {
    const point = closestOnSegment(at, graph.points[a], graph.points[b]);
    const gap = distance(point, at);
    if (gap > bestGap) continue;
    bestGap = gap;
    best = { edgeKey, point };
  }
  return best;
};

/** Put a corner ON a length — the ⊕ — so it can then be dragged into shape. */
export const splitGraphEdge: SplitGraphEdge = ({ graph, edgeKey, at }) => {
  const ends = graph.edges[edgeKey];
  if (!ends) return { graph, key: "" };
  const existing = nearestGraphNode({ graph, at, radius: COINCIDENT });
  if (existing) return { graph, key: existing };
  const key = freshKey(graph.points, at);
  const points = { ...graph.points, [key]: copy(at) };
  const edges = { ...graph.edges };
  delete edges[edgeKey];
  edges[edgeKeyOf(ends[0], key)] = [ends[0], key];
  edges[edgeKeyOf(key, ends[1])] = [key, ends[1]];
  return { graph: { ...graph, points, edges }, key };
};

/**
 * Resolve a POINT to a NODE — the one place a coordinate becomes topology.
 *
 * **Connectivity here is node identity, never geometry.** A point that merely
 * touches a length is not on it: the edges are node pairs, so a corner dropped
 * halfway along one connects to nothing, and no device's cable can traverse a
 * junction that does not exist. *"i take the joint and moved forward to the edge
 * but its not recognizing that they should become one route"* is exactly that.
 *
 * So landing on a bare length SPLITS it there and returns the new node. This is
 * the same defect, and the same weld-and-splice fix, the tray work hit on the
 * agent side: `tray_from_routes` left project 153's tray as 15 disconnected
 * components because a stub's foot lands mid-segment rather than on a node.
 *
 * A node beats a length within the same reach: joining two points that already
 * exist is the likelier intent and it mints nothing.
 */
export const landOnGraph: LandOnGraph = ({ graph, at, reach = COINCIDENT }) => {
  const onNode = nearestGraphNode({ graph, at, radius: reach });
  if (onNode) return { graph, key: onNode };
  const onEdge = nearestGraphEdge({ graph, at, radius: reach });
  if (onEdge) {
    return splitGraphEdge({
      graph,
      edgeKey: onEdge.edgeKey,
      at: onEdge.point,
    });
  }
  const key = freshKey(graph.points, at);
  return {
    graph: { ...graph, points: { ...graph.points, [key]: copy(at) } },
    key,
  };
};

/** Where on ONE length a point comes to rest — the perpendicular foot. */
export const footOnGraphEdge: FootOnGraphEdge = ({ graph, edgeKey, at }) => {
  const ends = graph.edges[edgeKey];
  if (!ends) return null;
  const a = graph.points[ends[0]];
  const b = graph.points[ends[1]];
  if (!a || !b) return null;
  return closestOnSegment(at, a, b);
};

/**
 * Two nodes become one, and every length that ran to either runs to the
 * survivor. This is what makes two pieces one route.
 *
 * The survivor is whichever is a fact of the SURVEY — the board, or a device —
 * because those are positions the run measured and a merge must never delete
 * one. Two of them never merge at all: they are joined by a length instead.
 */
export const mergeGraphNodes: MergeGraphNodes = ({ graph, from, into }) => {
  if (from === into) return graph;
  if (!graph.points[from] || !graph.points[into]) return graph;
  const fixed = new Set<NodeKey>([
    graph.boardKey,
    ...Object.values(graph.deviceKeys),
  ]);
  // Both are survey facts: joined, never merged.
  if (fixed.has(from) && fixed.has(into)) {
    return {
      ...graph,
      edges: { ...graph.edges, [edgeKeyOf(from, into)]: [from, into] },
    };
  }
  const [gone, kept] = fixed.has(from) ? [into, from] : [from, into];
  const edges: Record<EdgeKey, [NodeKey, NodeKey]> = {};
  for (const [a, b] of Object.values(graph.edges)) {
    const x = a === gone ? kept : a;
    const y = b === gone ? kept : b;
    // The length he dragged ALONG collapses to nothing and goes with it.
    if (x === y) continue;
    edges[edgeKeyOf(x, y)] = [x, y];
  }
  const points = { ...graph.points };
  delete points[gone];
  return pruned({ ...graph, points, edges });
};

/**
 * No node may lie inside a length. Cut every length that runs through one.
 *
 * The other half of `landOnGraph`: that stops a POINT floating over a length,
 * this stops a length running through a NODE. Both are the same rule — geometry
 * touching must mean topology joined — and it is the rule the tray weld needed
 * on the agent side for exactly the same reason.
 *
 * It also keeps what he sees BEFORE saving and after: rebuilding from stored
 * polylines welds by construction, so without this the live graph kept the leg
 * he drew lying whole across the trunk it had just joined, and the reload
 * quietly tidied it into two.
 */
export const weldGraph: WeldGraph = (graph) => {
  let working = graph;
  for (let pass = 0; pass < MAX_WELD_PASSES; pass += 1) {
    let cut = false;
    for (const [edgeKey, [a, b]] of Object.entries(working.edges)) {
      const from = working.points[a];
      const to = working.points[b];
      if (!from || !to) continue;
      for (const [key, point] of Object.entries(working.points)) {
        if (key === a || key === b) continue;
        // Strictly INSIDE it: an end of the length is already joined to it.
        if (distance(closestOnSegment(point, from, to), point) > COINCIDENT)
          continue;
        // Rewired here rather than through `splitGraphEdge`, which refuses to
        // cut where a node already sits — and a node already sitting there is
        // precisely the case this exists for.
        const edges = { ...working.edges };
        delete edges[edgeKey];
        edges[edgeKeyOf(a, key)] = [a, key];
        edges[edgeKeyOf(key, b)] = [key, b];
        working = { ...working, edges };
        cut = true;
        break;
      }
      if (cut) break;
    }
    if (!cut) return working;
  }
  return working;
};

export const connectGraphPath: ConnectGraphPath = ({ graph, points, reach }) => {
  let working = graph;
  const keys: NodeKey[] = [];

  for (const point of points) {
    const landed = landOnGraph({ graph: working, at: point, reach });
    working = landed.graph;
    keys.push(landed.key);
  }

  const edges = { ...working.edges };
  for (let i = 1; i < keys.length; i += 1) {
    if (keys[i] === keys[i - 1]) continue;
    edges[edgeKeyOf(keys[i - 1], keys[i])] = [keys[i - 1], keys[i]];
  }
  return { ...working, edges };
};

export const derivePaths: DerivePaths = (graph) => {
  const neighbours: Record<NodeKey, NodeKey[]> = {};
  for (const [a, b] of Object.values(graph.edges)) {
    (neighbours[a] ??= []).push(b);
    (neighbours[b] ??= []).push(a);
  }

  // Dijkstra from the board. Tens of nodes, so the plain scan is the right
  // shape: no heap to get wrong, and the order is deterministic.
  const cost: Record<NodeKey, number> = {};
  const cameFrom: Record<NodeKey, NodeKey | null> = {};
  const settled = new Set<NodeKey>();
  if (graph.boardKey) {
    cost[graph.boardKey] = 0;
    cameFrom[graph.boardKey] = null;
  }

  for (;;) {
    let next: NodeKey | null = null;
    for (const key of Object.keys(cost)) {
      if (settled.has(key)) continue;
      if (next === null || cost[key] < cost[next]) next = key;
    }
    if (next === null) break;
    settled.add(next);
    for (const other of neighbours[next] ?? []) {
      const through = cost[next] + distance(graph.points[next], graph.points[other]);
      if (cost[other] !== undefined && cost[other] <= through) continue;
      cost[other] = through;
      cameFrom[other] = next;
    }
  }

  const byDestination: Record<string, RoutePoint[]> = {};
  const unreachable: string[] = [];
  const used = new Set<EdgeKey>();

  for (const [destinationId, deviceKey] of Object.entries(graph.deviceKeys)) {
    const device = graph.points[deviceKey];
    if (cost[deviceKey] === undefined || !device) {
      // Its own position and nothing else: no cable is drawn for it and none is
      // measured, and the device stays exactly where the survey put it.
      byDestination[destinationId] = device ? [copy(device)] : [];
      unreachable.push(destinationId);
      continue;
    }
    const keys: NodeKey[] = [];
    for (let at: NodeKey | null = deviceKey; at !== null; at = cameFrom[at]) {
      keys.unshift(at);
    }
    for (let i = 1; i < keys.length; i += 1) used.add(edgeKeyOf(keys[i - 1], keys[i]));
    byDestination[destinationId] = keys.map((key) => copy(graph.points[key]));
  }

  return { byDestination, unreachable, usedEdgeKeys: [...used] };
};

export const edgeMarksOf: EdgeMarksOf = ({ graph, usedEdgeKeys }) => {
  const used = new Set(usedEdgeKeys);
  return Object.entries(graph.edges).map(([key, [a, b]]) => {
    const from = graph.points[a];
    const to = graph.points[b];
    const length = distance(from, to);
    const middle = { x: (from.x + to.x) / 2, y: (from.y + to.y) / 2 };
    return {
      key,
      a: copy(from),
      b: copy(to),
      x: middle.x,
      y: middle.y,
      hasAdd: length >= ADD_MIN_LENGTH,
      isUsed: used.has(key),
    };
  });
};

export const snapDragPoint: SnapDragPoint = ({ graph, key, position }) => {
  const neighbours: RoutePoint[] = [];
  for (const [a, b] of Object.values(graph.edges)) {
    if (a === key && graph.points[b]) neighbours.push(graph.points[b]);
    if (b === key && graph.points[a]) neighbours.push(graph.points[a]);
  }
  const alignedOn = (axis: "x" | "y"): number | null => {
    let best: number | null = null;
    let bestGap = SQUARE_SNAP;
    for (const neighbour of neighbours) {
      const gap = Math.abs(neighbour[axis] - position[axis]);
      if (gap > bestGap) continue;
      bestGap = gap;
      best = neighbour[axis];
    }
    return best;
  };
  const x = alignedOn("x");
  const y = alignedOn("y");
  if (x === null && y === null) return copy(position);
  // Squaring up on BOTH axes of the same neighbour would fold the two lengths
  // into nothing, so the axis he is further from is left where he put it.
  if (
    x !== null &&
    y !== null &&
    neighbours.some((n) => n.x === x && n.y === y)
  ) {
    return Math.abs(x - position.x) <= Math.abs(y - position.y)
      ? { x, y: position.y }
      : { x: position.x, y };
  }
  return { x: x ?? position.x, y: y ?? position.y };
};

/**
 * Where a free point of a stroke goes. It no longer hunts for a nearby node:
 * landing on the run is done by clicking the mark itself, so a snap here could
 * only ever drag him back to somewhere he did not click — which is exactly what
 * made two adjacent corners impossible to join.
 */
/**
 * Along x, then along y. Two nodes that already share a row or a column get one
 * straight length and no corner at all.
 */
export const orthogonalRun: OrthogonalRun = ({ from, to }) => {
  if (
    Math.abs(from.x - to.x) <= COINCIDENT ||
    Math.abs(from.y - to.y) <= COINCIDENT
  ) {
    return [copy(from), copy(to)];
  }
  return [copy(from), { x: to.x, y: from.y }, copy(to)];
};

/**
 * The lengths of the graph that no device's cable runs over, as bare
 * coordinates.
 *
 * This is exactly what `routeOverrides` cannot hold — every USED length is
 * already inside some destination's polyline — so it is exactly what has to be
 * stored beside it for a drawn cable to survive Done editing. Coordinates and
 * not keys, because keys are minted per session and mean nothing on reload.
 */
/**
 * A whole orthogonal run through every point he clicked, folded leg by leg.
 *
 * Two clicks still give the plain L — that path must never get harder — and
 * each extra click he places bends it again: *"this should be arbitrary not
 * just L-shape"*. Cable is pulled along the building, so every leg is still x
 * then y; what he chooses is where it turns.
 */
export const orthogonalPath: OrthogonalPath = (points) => {
  if (points.length < 2) return points.map(copy);
  const out: RoutePoint[] = [copy(points[0])];
  for (let i = 1; i < points.length; i += 1) {
    const leg = orthogonalRun({ from: out[out.length - 1], to: points[i] });
    // `leg[0]` is the point already at the end of `out`.
    out.push(...leg.slice(1));
  }
  return out;
};

export const strandedEdgesOf: StrandedEdgesOf = ({ graph, usedEdgeKeys }) => {
  const used = new Set(usedEdgeKeys);
  return Object.entries(graph.edges)
    .filter(([key]) => !used.has(key))
    .map(([, [from, to]]) => ({
      a: { ...graph.points[from] },
      b: { ...graph.points[to] },
    }))
    .filter((edge) => Boolean(edge.a) && Boolean(edge.b));
};
